
export class BTNode<T> {
	public left: BTNode<T>;
	public right: BTNode<T>;

	constructor(public data: T, public parent: BTNode<T>, public parentSide: Side) {

	}
	public level(): number {
		if (this.parent) {
			return this.parent.level() + 1;
		} else {
			return 0;
		}
	}
	public height(): number {
		return Math.max(this.leftHeight(), this.rightHeight())
	}
	public leftHeight(): number {
		return this.left ? this.left.height() + 1 : 0;
	}
	public rightHeight(): number {
		return this.right ? this.right.height() + 1 : 0;
	}
	public balanceFactor(): number {
		return this.leftHeight() - this.rightHeight();
	}
	public toString(): string {
		return `{ data:${this.data}, parent:${this.parent ? this.parent.data : '-'}(${this.parentSide ? this.parentSide.toUpperCase() : '-'}), left:${this.left ? this.left.data : '-'}, right:${this.right ? this.right.data : '-'} }`;
	}
}
enum Side {
	LEFT = 'left',
	RIGHT = 'right'
}
export class BinarySearchTree<T> {
	private resolve: (oldData: T, newData: T) => void;

	constructor(private comparator: (d0: T, d1: T) => number, resolve?: (nodeData: T, newData: T) => void, data?: T|T[] | BinarySearchTree<T>) {
		this.root = undefined;
		this.nodeCount = 0;
		if (resolve) {
			this.resolve = resolve;
		}
		if (data!==undefined) {
			
			this.addAll(data);
		}
	}

	private root: BTNode<T>;
	private nodeCount: number;

	public isEmpty(): boolean {
		return this.root === undefined;
	}
	public height(): number {
		return this.root !== undefined ? this.root.height() + 1 : 0;
	}
	public size(): number {
		return this.nodeCount;
	}
	public compare(d0: T, d1: T): number {
		return this.comparator(d0, d1);
	}
	public clear(): BinarySearchTree<T> {
		this.root = undefined;
		this.nodeCount = 0;
		return this;
	}
	public balance(force = true): BinarySearchTree<T> {
		if (this.root !== undefined && (force || (this.size() > 4 && Math.abs(this.root.balanceFactor()) > 3))) {
			this.root = this.balanceTree(this.root);
		}
		return this;
	}
	public addAll(data: T | T[] | BinarySearchTree<T>): number {
		if (data !== undefined) {
			if ((<T[]>data).push) {
				return (<T[]>data).filter(item => this.add(item) !== undefined).length;
			} else if ((<BinarySearchTree<T>>data).rotateLeftLeft) {
				return Array.from((<BinarySearchTree<T>>data).levelOrder()).filter(node => this.add(node.data) !== undefined).length;
			} else {
				return this.add(<T>data)!== undefined ? 1 : 0;
			}
		} else {
			return 0;
		}
	}
	public add(data: T): BTNode<T> {
		if (data !== undefined) {
			if (this.root === undefined) {
				this.root = new BTNode(data, undefined, undefined);
				this.nodeCount = 1;
				return this.root;
			} else {
				const node = this.insertNode(new BTNode(data, undefined, undefined), this.root);
				if (node !== undefined) {
					// this.root = this.balanceTree( node );
					this.nodeCount++;
				}
				return node;
			}
		} else {
			return undefined;
		}
	}
	private insertNode(node: BTNode<T>, parentNode: BTNode<T>): BTNode<T> {
		const compareResult = this.comparator(node.data, parentNode.data);
		if (compareResult < 0) {
			if (parentNode.left === undefined) {
				parentNode.left = node;
				node.parent = parentNode;
				node.parentSide = Side.LEFT;
				return node;
			} else {
				return this.insertNode(node, parentNode.left);
			}
		} else if (compareResult > 0) {
			if (parentNode.right === undefined) {
				parentNode.right = node;
				node.parent = parentNode;
				node.parentSide = Side.RIGHT;
				return node;
			} else {
				return this.insertNode(node, parentNode.right);
			}
		} else {
			//conflicting node
			if (this.resolve) {
				this.resolve(parentNode.data, node.data);
			}
    		return undefined;
		}
	}
	public min(): T {
		if (this.root === undefined) {
			return undefined;
		} else {
			let node = this.root;
			while (node.left !== undefined) {
				node = node.left;
			}
			return node.data;
		}
	}
	public max(): T {
		if (this.root === undefined) {
			return undefined;
		} else {
			let node = this.root;
			while (node.right !== undefined) {
				node = node.right;
			}
			return node.data;
		}
	}
	public prev(data: T): T {
		const node = this.prevNode(data, this.root);
		return node ? node.data : undefined;
	}
	public next(data: T): T {
		const node = this.nextNode(data, this.root);
		return node ? node.data : undefined;
	}
	public get(data: T): T {
		const node = this.getNode(data);
		return node ? node.data : undefined;
	}
	public *between(data0: T, data1: T): IterableIterator<T> {
		let node = this.nextNode(data0, this.root);
		while (node !== undefined && this.comparator(data1, node.data) !== 0) {
			yield node.data;
			node = this.nextNode(node.data, node);
		}
	}
	private minNode(node: BTNode<T>): BTNode<T> {
		if (node === undefined) {
			return undefined;
		} else {
			let minNode = node;
			while (minNode.left !== undefined) {
				minNode = minNode.left;
			}
			return minNode;
		}
	}
	private maxNode(node: BTNode<T>): BTNode <T>{
		if (node === undefined) {
			return undefined;
		} else {
			let maxNode = node;
			while (maxNode.right !== undefined) {
				maxNode = maxNode.right;
			}
			return maxNode;
		}
	}
	public getNode(data: T): BTNode<T> {
		let node = this.root;
		let compareResult: number;
		while (node) {
			compareResult = this.comparator(data, node.data);
			if (compareResult === 0) {
				return node;
			} else {
				node = compareResult < 0 ? node.left : node.right;
			}
		}
		return undefined;
	}
	public prevNode(data: T, node: BTNode<T>, prevCandidate?: BTNode<T>): BTNode<T> {
		if (node !== undefined) {
			const compareResult = this.comparator(data, node.data);
			if (compareResult === 0) {
				if (node.left) {
					return this.maxNode(node.left);
				} else {
					return prevCandidate;
				}
			} else if (compareResult < 0) {
				return this.prevNode(data, node.left, prevCandidate);
			} else {
				return this.prevNode(data, node.right, node);
			}
		} else {
			return undefined;
		}
	}
	public nextNode(data: T, node: BTNode<T>, nextCandidate?: BTNode<T>): BTNode<T> {
		if (node !== undefined) {
			const compareResult = this.comparator(data, node.data);
			if (compareResult === 0) {
				if (node.right) {
					return this.minNode(node.right);
				} else {
					return nextCandidate;
				}
			} else if (compareResult > 0) {
				return this.nextNode(data, node.right, nextCandidate);
			} else {
				return this.nextNode(data, node.left, node);
			}
		} else {
			return undefined;
		}
	}
	public has(data: T) {
		return this.getNode(data) !== undefined;
	}
	
	public deleteAll(data: T| T[] | BinarySearchTree<T>): T[] {
		if (data !== undefined) {
			if ((<T[]>data).push) {
				return (<T[]>data).filter(item => this.delete(item) !== undefined);
			} else if ((<BinarySearchTree<T>>data).rotateLeftLeft) {
				return Array.from((<BinarySearchTree<T>>data).levelOrder()).filter(node => this.delete(node.data) !== undefined).map( node => node.data);
			} else {
				return [ this.delete(<T>data) ];
			}
		} else {
			return [];
		}
	}
	
	public delete(data: T): T {
		if (data !== undefined) {
			let result = this.removeNode(data, this.root);
			if (result.removed) {
				this.nodeCount--;
				this.root = result.replaceNode;
				if (result.parentDeletedNode) {
					//console.log('Balance node %s', result.parentDeletedNode.data.toString());
					//                   this.root = this.balanceTree( result.parentDeletedNode );
				} else {
					//the old root was deleted
					//make sure that the new root (if any) does not have a parent
					if (this.root) {
						this.root.parent = undefined;
						this.root.parentSide = undefined;
						//console.log('Tree only contains a root node');
					} else {
						//console.log('Tree is empty');
					}
				}
			}
			return result.data;
		} else {
			return undefined;
		}
	}
	private removeNode(data: T, node: BTNode<T>): { removed: boolean, data: T, replaceNode: BTNode<T>, parentDeletedNode: BTNode<T> } {
		let result: { removed: boolean, data: T, replaceNode: BTNode<T>, parentDeletedNode: BTNode<T> };
		if (node === undefined) {
			result = { removed: false, data: undefined, replaceNode: undefined, parentDeletedNode: undefined };
			//            return result
		} else {
			const compareResult = this.comparator(data, node.data);
			if (compareResult === 0) {
				result = { removed: true, data: node.data, replaceNode: undefined, parentDeletedNode: node.parent };
				// node has at least one child 
				if (node.left !== undefined || node.right !== undefined) {
					if (node.left === undefined) {
						// node has no left child 
						result.replaceNode = node.right;
					} else if (node.right === undefined) {
						// node has no right child 
						result.replaceNode = node.left;
					} else {
						// node has two children
						//look for min value in right sub-tree
						var tempNode = node.right;
						while (tempNode.left !== undefined) tempNode = tempNode.left;

						node.data = tempNode.data;
						node.right = this.removeNode(tempNode.data, node.right).replaceNode;
						if (node.right) {
							node.right.parent = node;
							node.right.parentSide = Side.RIGHT;
						}
						result.replaceNode = node;
					}
				}
			} else if (compareResult < 0) {
				result = this.removeNode(data, node.left);
				node.left = result.replaceNode;
				if (node.left) {
					node.left.parent = node;
					node.left.parentSide = Side.LEFT;
				}
				result.replaceNode = node;
			} else {
				result = this.removeNode(data, node.right);
				node.right = result.replaceNode;
				if (node.right) {
					node.right.parent = node;
					node.right.parentSide = Side.RIGHT;
				}
				result.replaceNode = node;
			}
		}
		return result;
	}

	public *inOrder(): IterableIterator<T> {
		if (this.root === undefined) {
			return undefined;
		} else {
			yield* this.traverseInOrderIterator(this.root);
		};
	}
	private *traverseInOrderIterator(node: BTNode<T>): IterableIterator<T> {
		if (node.left !== undefined) yield* this.traverseInOrderIterator(node.left);
		yield node.data;
		if (node.right !== undefined) yield* this.traverseInOrderIterator(node.right);
	}
	public *preOrder(): IterableIterator<T> {
		if (this.root === undefined) {
			return undefined;
		} else {
			yield* this.traversePreOrderIterator(this.root);
		};
	}
	private *traversePreOrderIterator(node: BTNode<T>): IterableIterator<T> {
		yield node.data;
		if (node.left !== undefined) yield* this.traverseInOrderIterator(node.left);
		if (node.right !== undefined) yield* this.traverseInOrderIterator(node.right);
	}
	public *postOrder(): IterableIterator<T> {
		if (this.root === undefined) {
			return undefined;
		} else {
			yield* this.traversePostOrderIterator(this.root);
		};
	}
	private *traversePostOrderIterator(node: BTNode<T>): IterableIterator<T> {
		if (node.left !== undefined) yield* this.traverseInOrderIterator(node.left);
		if (node.right !== undefined) yield* this.traverseInOrderIterator(node.right);
		yield node.data;
	}
	public *levelOrder(): IterableIterator<BTNode<T>> {
		if (this.root !== undefined) {
			let node: BTNode<T>;
			let queue: BTNode<T>[] = [];
			queue.push(this.root);
			while (queue.length > 0) {
				node = queue.shift();
				yield node;
				if (node.left !== undefined) queue.push(node.left);
				if (node.right !== undefined) queue.push(node.right);
			}
		}
	}

	private linkToParent(newChild: BTNode<T>, parent: BTNode<T>, side: Side): BTNode<T> {
		if (parent) {
			parent[side] = newChild;
			newChild.parent = parent;
			newChild.parentSide = side;
		} else {
			newChild.parent = undefined;
			newChild.parentSide = undefined;
		}
		return newChild;
	}
	private rotateLeftLeft(node: BTNode<T>): BTNode<T> {
		let newTopNode = this.linkToParent(node.right, node.parent, node.parentSide);
		this.linkToParent(node, this.minNode(newTopNode), Side.LEFT).right = undefined;
		return newTopNode;
	}
	private rotateRightRight(node: BTNode<T>): BTNode<T> {
		let newTopNode = this.linkToParent(node.left, node.parent, node.parentSide);
		this.linkToParent(node, this.maxNode(newTopNode), Side.RIGHT).left = undefined;
		return newTopNode;
	}
	private rotateLeftRight(node: BTNode<T>): BTNode<T> {
		this.rotateLeftLeft(node.left);
		return this.rotateRightRight(node);
	}
	private rotateRightLeft(node: BTNode<T>): BTNode<T> {
		this.rotateRightRight(node.right);
		return this.rotateLeftLeft(node);
	}
	private balanceNode(node: BTNode<T>): BTNode<T> {
		if (node.balanceFactor() > 1) {
			// left subtree is higher than right subtree
			if (node.left.balanceFactor() < 0) {
				return this.rotateLeftRight(node);
			} else {
				return this.rotateRightRight(node);
			}
		} else if (node.balanceFactor() < -1) {
			// right subtree is higher than left subtree
			if (node.right.balanceFactor() > 0) {
				return this.rotateRightLeft(node);
			} else {
				return this.rotateLeftLeft(node);
			}
		} else {
			return node;
		}
	}
	private balanceTree(node: BTNode<T>): BTNode<T> {
		let current = node;
		let newParent = undefined;
		while (current) {
			newParent = this.balanceNode(current);
			current = current.parent;
		}
		return newParent;
	}

	public toInOrderString(dataFormatter?: (data: T) => string): string {
		let inOrderData: string[] = [];
		for (let data of this.inOrder()) {
			inOrderData.push(dataFormatter ?  dataFormatter(data) :  data.toString());
		}
		return inOrderData.join(' → ');
	}

	public toString(requestedDataWidth: number, layoutHeight?: number, margin?: number): string {
		return this.toFormattedString(requestedDataWidth, (data: T) => ('' + data), layoutHeight, margin);
	}

	public toFormattedString(requestedDataWidth: number, dataFormatter: (data: T) => string, layoutHeight?: number, margin?: number): string {
		let logString = `\nn=${this.size()}\nh=${this.height()}`;
		if (this.root !== undefined) {
			const _margin = margin !== undefined && margin >= 0 ? margin : 1;
			let height = this.root.height() + 1;
			height = layoutHeight !== undefined && layoutHeight > height ? layoutHeight : height;
			let dataWidth = requestedDataWidth + ((requestedDataWidth % 2) === 0 ? (_margin % 2) === 0 ? 1 : 0 : (_margin % 2) === 0 ? 0 : 1);//make sure that dataWidth + margin is uneven
			let baseWidth = dataWidth + _margin + 1;//base width is even
			let totalWidth = Math.pow(2, height - 1) * baseWidth;
			let undefinedCell = ''.padStart(dataWidth, '_');

			const nodeToString = (node: BTNode<T>): string => {
				return (node.data ? ((node.parentSide === Side.RIGHT ? '↖' : '') +
					dataFormatter(node.data).toString().substring(0, dataWidth).replace(/ /g, '_').padEnd(dataWidth, '_') +
					(node.parentSide === Side.LEFT ? '↗' : '') +
					(node.parentSide === undefined ? '_' : '')) : ''.padStart(dataWidth + 1, ' ')) +
					''.padStart(_margin, ' ');
			};

			let node: BTNode<T>;
			let queue: BTNode<T>[] = [];
			let maxItemsInLevel: number;
			let levelSpacing: number;
			let spacing: number;
			let curLevel = -1;
			let nodeLevel = -1;

			queue.push(this.root);
			while (queue.length > 0) {
				node = queue.shift();
				nodeLevel = node.level();
				if (nodeLevel != curLevel) {
					//calculate new levelSpacing
					curLevel = nodeLevel;
					maxItemsInLevel = Math.pow(2, nodeLevel);
					levelSpacing = (totalWidth - baseWidth * maxItemsInLevel) / maxItemsInLevel;
					spacing = levelSpacing / 2;
					logString += '\n';
				} else {
					spacing = levelSpacing;
				}
				//build string
				logString += ''.padStart(spacing, ' ') + nodeToString(node);
				if (node.left !== undefined || height - nodeLevel >= 2) queue.push(node.left ? node.left : new BTNode(undefined, node, Side.LEFT));
				if (node.right !== undefined || height - nodeLevel >= 2) queue.push(node.right ? node.right : new BTNode(undefined, node, Side.RIGHT));
			}
		}
		return logString;
	}
}



